import React, { useState, useEffect } from 'react';
import { MessageSquarePlus, Send, CheckCircle, Clock, MessageCircle, Shield, Trash2 } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FeedbackPage = ({ getAuthHeaders, currentUser }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', content: '', category: 'general', anonymous: false });
  const [respondingTo, setRespondingTo] = useState(null);
  const [response, setResponse] = useState('');
  const isMgmt = ['admin','executive','superadmin','manager'].includes(currentUser?.role);
  const categories = ['general', 'improvement', 'complaint', 'praise'];
  const catColors = { general: 'cyan', improvement: 'emerald', complaint: 'red', praise: 'amber' };

  useEffect(() => { fetch(); }, []);
  const fetch = async () => { try { const r = await axios.get(`${BACKEND_URL}/api/engagement/feedback`, { headers: getAuthHeaders() }); setItems(r.data); } catch(e) {} finally { setLoading(false); } };
  const submit = async () => {
    if (!form.title || !form.content) { toast.error('Please fill all fields'); return; }
    try { await axios.post(`${BACKEND_URL}/api/engagement/feedback`, form, { headers: getAuthHeaders() }); toast.success('Feedback submitted'); setShowForm(false); setForm({ title: '', content: '', category: 'general', anonymous: false }); fetch(); } catch(e) { toast.error('Failed'); }
  };
  const respond = async () => {
    if (!response.trim()) return;
    try { await axios.post(`${BACKEND_URL}/api/engagement/feedback/${respondingTo}/respond`, { response }, { headers: getAuthHeaders() }); toast.success('Response sent'); setRespondingTo(null); setResponse(''); fetch(); } catch(e) { toast.error('Failed'); }
  };

  return (
    <div className="space-y-6" data-testid="feedback-page">
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-slate-900 via-teal-950/30 to-slate-900 border border-teal-500/20 p-6">
        <div className="absolute top-0 right-0 w-64 h-64 bg-teal-500/10 rounded-full blur-3xl" />
        <div className="relative flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-teal-500/30 to-emerald-600/30 flex items-center justify-center border border-teal-500/20"><MessageSquarePlus className="w-7 h-7 text-teal-400" /></div>
            <div><h1 className="text-2xl font-bold text-white">Feedback & Suggestions</h1><p className="text-slate-400 text-sm">Share your thoughts anonymously or openly</p></div>
          </div>
          <Button onClick={() => setShowForm(true)} className="bg-gradient-to-r from-teal-500 to-emerald-600 text-white"><Send className="w-4 h-4 mr-2" />Submit Feedback</Button>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4">
        {categories.map(cat => (
          <div key={cat} className="bg-slate-900/60 border border-slate-800/50 rounded-xl p-4 text-center">
            <p className={`text-2xl font-bold text-${catColors[cat]}-400`}>{items.filter(i => i.category === cat).length}</p>
            <p className="text-slate-500 text-xs capitalize">{cat}</p>
          </div>
        ))}
      </div>

      {loading ? <div className="text-center py-16 text-slate-500">Loading...</div> : items.length === 0 ? (
        <div className="text-center py-16 bg-slate-900/40 border border-slate-800/50 rounded-2xl"><MessageCircle className="w-12 h-12 text-slate-700 mx-auto mb-3" /><p className="text-slate-500">No feedback yet</p></div>
      ) : (
        <div className="space-y-3">
          {items.map(fb => (
            <div key={fb.id} className="bg-slate-900/60 border border-slate-800/50 rounded-xl p-5 hover:border-teal-500/30 transition-all">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-white font-semibold">{fb.title}</h3>
                    <Badge className={`bg-${catColors[fb.category]}-500/20 text-${catColors[fb.category]}-400`}>{fb.category}</Badge>
                    <Badge className={fb.status === 'responded' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}>{fb.status}</Badge>
                    {fb.anonymous && <Badge className="bg-slate-500/20 text-slate-400"><Shield className="w-3 h-3 mr-1" />Anonymous</Badge>}
                  </div>
                  <p className="text-slate-400 text-sm whitespace-pre-wrap">{fb.content}</p>
                  {fb.response && (
                    <div className="mt-3 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                      <p className="text-emerald-300 text-sm"><span className="font-semibold">Response from {fb.responded_by}:</span> {fb.response}</p>
                    </div>
                  )}
                  <div className="flex items-center gap-3 mt-3 text-xs text-slate-600">
                    <span>By {fb.author}</span><span>{new Date(fb.created_at).toLocaleDateString('en-GB')}</span>
                  </div>
                </div>
                {isMgmt && fb.status !== 'responded' && (
                  <Button size="sm" onClick={() => { setRespondingTo(fb.id); setResponse(''); }} className="bg-teal-500/20 text-teal-400 hover:bg-teal-500/30 ml-3"><MessageCircle className="w-3.5 h-3.5 mr-1" />Reply</Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={() => setShowForm(false)}>
        <DialogContent className="bg-slate-950 border-teal-500/20 text-white max-w-lg">
          <DialogHeader><DialogTitle className="text-teal-400">Submit Feedback</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="Subject" className="w-full px-4 py-3 bg-slate-900/80 border border-slate-700/50 rounded-xl text-white focus:outline-none focus:border-teal-500/50" />
            <select value={form.category} onChange={e => setForm({...form, category: e.target.value})} className="w-full px-4 py-3 bg-slate-900/80 border border-slate-700/50 rounded-xl text-white focus:outline-none">
              {categories.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase()+c.slice(1)}</option>)}
            </select>
            <textarea value={form.content} onChange={e => setForm({...form, content: e.target.value})} rows={5} placeholder="Your feedback..." className="w-full px-4 py-3 bg-slate-900/80 border border-slate-700/50 rounded-xl text-white focus:outline-none resize-none" />
            <label className="flex items-center gap-2 text-slate-300 text-sm"><input type="checkbox" checked={form.anonymous} onChange={e => setForm({...form, anonymous: e.target.checked})} className="rounded" /><Shield className="w-4 h-4" />Submit anonymously</label>
            <div className="flex justify-end gap-3"><Button onClick={() => setShowForm(false)} variant="outline" className="border-slate-700 text-slate-400">Cancel</Button><Button onClick={submit} className="bg-gradient-to-r from-teal-500 to-emerald-600 text-white">Submit</Button></div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!respondingTo} onOpenChange={() => setRespondingTo(null)}>
        <DialogContent className="bg-slate-950 border-teal-500/20 text-white max-w-md">
          <DialogHeader><DialogTitle className="text-teal-400">Respond to Feedback</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <textarea value={response} onChange={e => setResponse(e.target.value)} rows={4} placeholder="Your response..." className="w-full px-4 py-3 bg-slate-900/80 border border-slate-700/50 rounded-xl text-white focus:outline-none resize-none" />
            <div className="flex justify-end gap-3"><Button onClick={() => setRespondingTo(null)} variant="outline" className="border-slate-700 text-slate-400">Cancel</Button><Button onClick={respond} className="bg-teal-500 text-white">Send Response</Button></div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
export default FeedbackPage;
